import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const skillsOptions = [
  'Teaching & Education',
  'Healthcare & Medical',
  'Content Writing',
  'Photography & Videography',
  'Social Media Management',
  'Event Management',
  'Data Entry & Administration',
  'Cooking & Food Service',
  'Transportation & Logistics',
  'Fundraising',
  'Counseling',
  'IT & Technical Support',
  'Graphic Design',
  'Public Speaking',
  'Legal Aid',
  'Other',
];

const availabilityOptions = [
  'Weekdays - Morning',
  'Weekdays - Afternoon',
  'Weekdays - Evening',
  'Weekends Only',
  'Full Time',
  'Flexible / On-Call',
  'Remote / Online Only',
];

const cityOptions = [
  'Delhi NCR', 'Mumbai', 'Bengaluru', 'Chennai', 'Hyderabad', 'Kolkata',
  'Jaipur', 'Lucknow', 'Ahmedabad', 'Pune', 'Bhopal', 'Patna',
  'Chandigarh', 'Indore', 'Nagpur', 'Other',
];

export default function VolunteerPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    skills: [] as string[],
    availability: '',
    experience: '',
    message: '',
    website_alt: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSkill = (skill: string) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.includes(skill)
        ? prev.skills.filter((s) => s !== skill)
        : [...prev.skills, skill],
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus('idle');
    setFormError('');

    if (!formData.fullName || !formData.email || !formData.phone || !formData.city) {
      setFormError('Please fill in all required fields.');
      setSubmitStatus('error');
      return;
    }

    if (formData.skills.length === 0) {
      setFormError('Please select at least one skill.');
      setSubmitStatus('error');
      return;
    }

    const honeypot = (e.currentTarget.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitStatus('success');
      setFormData({ fullName: '', email: '', phone: '', city: '', skills: [], availability: '', experience: '', message: '', website_alt: '' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = new FormData(e.currentTarget);
      const response = await fetch('https://readdy.ai/api/form/d9eud4frf0c7mtq82t6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(payload as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setSubmitStatus('success');
        setFormData({ fullName: '', email: '', phone: '', city: '', skills: [], availability: '', experience: '', message: '', website_alt: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg && serverMsg.includes('spam')) {
          setSubmitStatus('success');
          setFormData({ fullName: '', email: '', phone: '', city: '', skills: [], availability: '', experience: '', message: '', website_alt: '' });
        } else {
          setFormError(serverMsg || 'Something went wrong. Please try again.');
          setSubmitStatus('error');
        }
      }
    } catch {
      setFormError('Network error. Please check your connection and try again.');
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-20 pb-12 md:pt-28 md:pb-16 bg-gradient-to-br from-secondary-500 via-secondary-400 to-accent-400 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/20"></div>
          <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl"></div>
          <div className="relative max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 text-center">
            <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur-sm text-white/90 text-xs font-medium rounded-full mb-4">
              <i className="ri-team-line mr-1.5"></i>Join Our Mission
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4">Become a Volunteer</h1>
            <p className="text-white/80 text-sm md:text-lg max-w-2xl mx-auto">
              Join 2,500+ changemakers across India. Your time and skills can transform lives — from teaching children to organizing medical camps and everything in between.
            </p>
          </div>
        </section>

        <section className="py-12 md:py-18 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
              {/* Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl p-6 md:p-8 border border-background-200">
                  {submitStatus === 'success' ? (
                    <div className="text-center py-10">
                      <div className="w-20 h-20 mx-auto mb-5 rounded-full bg-green-100 flex items-center justify-center">
                        <i className="ri-check-line text-green-600 text-3xl"></i>
                      </div>
                      <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-2">Thank You for Signing Up!</h2>
                      <p className="text-foreground-500 text-sm max-w-md mx-auto mb-6">
                        Your volunteer registration has been received. Our team will review your application and reach out within 5-7 business days to discuss opportunities matching your skills and availability.
                      </p>
                      <button
                        onClick={() => setSubmitStatus('idle')}
                        className="px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors"
                      >
                        Register Another Volunteer
                      </button>
                    </div>
                  ) : (
                    <>
                      <h2 className="font-heading text-xl font-bold text-foreground-950 mb-1">Volunteer Registration</h2>
                      <p className="text-foreground-500 text-sm mb-6">Fill in your details and our team will get in touch with volunteering opportunities.</p>

                      {submitStatus === 'error' && formError && (
                        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>
                      )}

                      <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-5">
                        {/* Honeypot */}
                        <div className="absolute opacity-0 pointer-events-none" style={{ position: 'absolute', left: '-9999px' }}>
                          <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Full Name *</label>
                            <input
                              type="text"
                              name="fullName"
                              value={formData.fullName}
                              onChange={(e) => updateField('fullName', e.target.value)}
                              required
                              placeholder="Enter your full name"
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Email Address *</label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={(e) => updateField('email', e.target.value)}
                              required
                              placeholder="your@email.com"
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Phone Number *</label>
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={(e) => updateField('phone', e.target.value)}
                              required
                              placeholder="+91 98765 43210"
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100 transition-all text-foreground-900 placeholder:text-foreground-400"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">City *</label>
                            <select
                              name="city"
                              value={formData.city}
                              onChange={(e) => updateField('city', e.target.value)}
                              required
                              className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100 transition-all text-foreground-900 appearance-none cursor-pointer"
                              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                            >
                              <option value="">Select your city</option>
                              {cityOptions.map((city) => (
                                <option key={city} value={city}>{city}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-foreground-600 mb-2 block">Skills & Expertise * (select all that apply)</label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {skillsOptions.map((skill) => (
                              <button
                                key={skill}
                                type="button"
                                onClick={() => toggleSkill(skill)}
                                className={`text-left px-3 py-2.5 rounded-xl text-xs font-medium border transition-all ${
                                  formData.skills.includes(skill)
                                    ? 'border-secondary-500 bg-secondary-50 text-secondary-700'
                                    : 'border-background-200 bg-background-50 text-foreground-600 hover:border-secondary-300'
                                }`}
                              >
                                {skill}
                              </button>
                            ))}
                          </div>
                          {formData.skills.length > 0 && (
                            <input type="hidden" name="skills" value={formData.skills.join(', ')} />
                          )}
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Availability</label>
                          <select
                            name="availability"
                            value={formData.availability}
                            onChange={(e) => updateField('availability', e.target.value)}
                            className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100 transition-all text-foreground-900 appearance-none cursor-pointer"
                            style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                          >
                            <option value="">Select availability</option>
                            {availabilityOptions.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Previous Volunteer Experience</label>
                          <textarea
                            name="experience"
                            value={formData.experience}
                            onChange={(e) => updateField('experience', e.target.value)}
                            placeholder="Tell us about any previous volunteering or relevant experience..."
                            maxLength={500}
                            rows={3}
                            className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100 transition-all text-foreground-900 placeholder:text-foreground-400 resize-none"
                          ></textarea>
                          <p className="text-xs text-foreground-400 mt-1">{500 - formData.experience.length} characters remaining</p>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-foreground-600 mb-1.5 block">Why do you want to volunteer?</label>
                          <textarea
                            name="message"
                            value={formData.message}
                            onChange={(e) => updateField('message', e.target.value)}
                            placeholder="Share why you'd like to join Happy Faces Society..."
                            maxLength={500}
                            rows={3}
                            className="w-full px-4 py-3 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-secondary-400 focus:ring-2 focus:ring-secondary-100 transition-all text-foreground-900 placeholder:text-foreground-400 resize-none"
                          ></textarea>
                          <p className="text-xs text-foreground-400 mt-1">{500 - formData.message.length} characters remaining</p>
                        </div>

                        <button
                          type="submit"
                          disabled={submitting}
                          className="w-full py-3.5 bg-secondary-500 text-white text-sm font-bold rounded-xl hover:bg-secondary-600 disabled:bg-foreground-300 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          {submitting ? (
                            <>
                              <i className="ri-loader-4-line animate-spin"></i> Submitting...
                            </>
                          ) : (
                            <>
                              <i className="ri-send-plane-line"></i> Submit Registration
                            </>
                          )}
                        </button>
                      </form>
                    </>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 border border-background-200">
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">Why Volunteer?</h3>
                  <div className="space-y-4">
                    {[
                      { icon: 'ri-heart-line', title: 'Make Real Impact', desc: 'Directly contribute to changing lives in underserved communities.' },
                      { icon: 'ri-group-line', title: 'Join a Community', desc: 'Connect with 2,500+ passionate changemakers across India.' },
                      { icon: 'ri-award-line', title: 'Gain Experience', desc: 'Build skills, earn certificates, and grow professionally.' },
                      { icon: 'ri-calendar-check-line', title: 'Flexible Commitment', desc: 'Choose your hours — from a few hours a week to full-time.' },
                    ].map((item, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="w-9 h-9 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                          <i className={`${item.icon} text-secondary-600 text-sm`}></i>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-foreground-900">{item.title}</div>
                          <div className="text-xs text-foreground-500">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-br from-secondary-100 to-accent-100 rounded-2xl p-6 border border-secondary-200">
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-3">
                    <i className="ri-team-line text-secondary-600 text-2xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">2,500+ Volunteers Strong</h3>
                  <p className="text-foreground-600 text-xs leading-relaxed mb-3">Our volunteer network spans 150+ cities. From students to retirees, every volunteer brings unique skills and passion.</p>
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-map-pin-line text-secondary-600"></i>
                    <span>Active in 12 states across India</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}